import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../../services/authService';
import useAuth from '../../hooks/useAuth';
import backgroundImage from '../../assets/background.png';
import defaultAvatar from '../../assets/default-avatar.png';
import './Register.css';

const Register = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    cpf: '',
    birthDate: '',
    role: 'Jogador',
    phone: ''
  });

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(defaultAvatar);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  
  // NOVO: Adiciona um estado para a visibilidade do CPF
  const [cpfVisible, setCpfVisible] = useState(true);

  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();

  const availableRoles = [
    { value: 'Jogador', label: 'Jogador' },
    { value: 'Técnico', label: 'Técnico, Auxiliar Técnico' },
    { value: 'Diretor', label: 'Diretor' }
    // Removidas as opções: Árbitro, Narrador, Gestor
  ];

  const formatCPF = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatPhone = (value) => {
    if (!value) return '';
    value = value.replace(/\D/g, '');
    if (value.length > 10) {
      return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else {
      return value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
  };

  useEffect(() => {
    if (user) {
      const redirectPath = new URLSearchParams(location.search).get('redirectAfterLogin');
      navigate(redirectPath || '/dashboard', { replace: true });
    }
  }, [user, navigate, location]);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    if (form.cpf.replace(/\D/g, '').length !== 11) {
      setError('CPF inválido');
      return;
    }

    setIsLoading(true);
    
    const formData = new FormData();
    formData.append('name', form.name);
    formData.append('email', form.email);
    formData.append('password', form.password);
    formData.append('cpf', form.cpf.replace(/\D/g, ''));
    formData.append('birthDate', form.birthDate);
    formData.append('role', form.role);
    
    if (form.phone) {
      formData.append('phone', form.phone.replace(/\D/g, ''));
    }

    if (avatarFile) {
      formData.append('avatar', avatarFile);
    }

    try {
      const response = await api.register(formData);

      if (response.success) {
        const redirectPath = new URLSearchParams(location.search).get('redirectAfterLogin');
        navigate(`/login?redirect=${redirectPath || ''}`, { replace: true });
      } else {
        setError(response.error || 'Erro ao cadastrar');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro no servidor');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="register-page"
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
    >
      <div className="register-overlay"></div>
      <div className="register-card">
        <h1>Criar conta</h1>

        {error && <div className="alert error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="avatar-upload">
            <label htmlFor="avatar-input">
              <img src={avatarPreview} alt="Avatar Preview" className="avatar-preview" />
              <span>Escolher foto</span>
            </label>
            <input
              id="avatar-input"
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
            />
          </div>

          <div className="form-group">
            <label>Nome completo*</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Email*</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Senha*</label>
            <div className="password-wrapper">
              <input
                type={passwordVisible ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                minLength="8"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setPasswordVisible(!passwordVisible)}
              >
                {passwordVisible ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Confirmar senha*</label>
            <div className="password-wrapper">
              <input
                type={confirmPasswordVisible ? 'text' : 'password'}
                value={form.confirmPassword}
                onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                minLength="8"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
              >
                {confirmPasswordVisible ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          
          {/* MODIFICADO: Campo de CPF agora com wrapper e botão de alternância */}
          <div className="form-group">
            <label>CPF*</label>
            <div className="password-wrapper">
              <input
                type={cpfVisible ? 'text' : 'password'}
                value={formatCPF(form.cpf)}
                onChange={(e) => setForm({ ...form, cpf: e.target.value })}
                maxLength="14"
                placeholder="000.000.000-00"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setCpfVisible(!cpfVisible)}
              >
                {cpfVisible ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Telefone (Opcional)</label>
            <input
              type="tel"
              value={formatPhone(form.phone)}
              onChange={(e) => setForm({ ...form, phone: e.target.value.slice(0, 15) })}
              maxLength="15"
              placeholder="(99) 99999-9999"
            />
          </div>

          <div className="form-group">
            <label>Data de nascimento*</label>
            <input
              type="date"
              value={form.birthDate}
              onChange={(e) => setForm({ ...form, birthDate: e.target.value })}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div className="form-group">
            <label>Função*</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              required
            >
              {availableRoles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="primary-button"
            disabled={isLoading}
          >
            {isLoading ? 'Cadastrando...' : 'Cadastrar'}
          </button>
        </form>

        <div className="auth-links">
          Já tem uma conta? <Link to="/login">Faça login</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;